/**
 * Per-tenant Statements tab (TMMRSL-0029, H §7.2 / §7.4): this tenant's **subscriber statements**
 * (`GET …/tenants/{slug}/statements?status=&period=`, the month filter TMMRSL-0073) and, for the
 * selected one, its lines with the CSV
 * export. The tenant-as-payee parts of the tenant console (payout table, payable bars, revenue
 * tiles, "my share") are gone: the reseller is the payee, and its payouts are on `/settlements`.
 */
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { StatementView } from '@/api/generated';
import { ApiErrorState } from '@/components/ApiErrorState';
import { MoneyCell, NumericCell } from '@/components/cells';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { enumParam, useUrlState, type UrlParam } from '@/components/useUrlState';
import { StatusChip } from '@/components/status/StatusChip';
import { formatCivilMonth } from '@/i18n/format';
import { isCivilMonth, recentMonths, todayCivil } from '@/i18n/periods';
import { useCivilRange } from '@/i18n/useCivilRange';
import { useLocale } from '@/i18n/useLocale';
import { FIELD_WIDTH } from '@/theme';
import { useTenant, useTenantLink } from '@/tenant/useTenant';
import { StatementLines } from './StatementLines';
import { useStatements } from './useStatements';

const STATUSES = ['PENDING', 'PAID', 'ACTION_REQUIRED', 'FAILED', 'ATTENTION', 'WAIVED'] as const;

/** How many months the period filter offers, newest first. */
const PERIOD_MONTHS = 24;

/** A month (`YYYY-MM`); anything else reads as All periods. */
const monthParam: UrlParam<string> = {
  defaultValue: '',
  parse: (raw) => (isCivilMonth(raw) ? raw : undefined),
  serialize: (value) => value,
};

/**
 * The filters in the URL (`?status=`, `?period=`, TMM-B6); all statuses and All periods are the
 * omitted defaults.
 */
const SCHEMA = { status: enumParam(['', ...STATUSES], ''), period: monthParam };

function newestFirst(rows: StatementView[]): StatementView[] {
  return [...rows].sort((a, b) =>
    b.periodEnd.localeCompare(a.periodEnd),
  );
}

export function StatementsScreen() {
  const { t } = useTranslation();
  const locale = useLocale();
  const navigate = useNavigate();
  // A statement's link, and the row that closes it, keep the list's query (TMM-B6).
  const { search } = useLocation();
  const params = useParams();
  const tenant = useTenant();
  const link = useTenantLink();
  const url = useUrlState(SCHEMA);
  const filter = url.values.status;
  const period = url.values.period;
  const { status, data, error, reload } = useStatements(
    filter === '' ? undefined : filter,
    period === '' ? undefined : period,
  );
  // A month from the URL is an option even when it is older than the list.
  const recent = recentMonths(PERIOD_MONTHS, todayCivil());
  const months = period && !recent.includes(period) ? [...recent, period] : recent;
  const filtered = filter !== '' || period !== '';
  const rawId = params.id !== undefined ? Number(params.id) : undefined;
  const selectedId = rawId !== undefined && Number.isFinite(rawId) ? rawId : undefined;
  const rows = newestFirst(data?.statements ?? []);
  const selected = rows.find((row) => row.id === selectedId);
  const dash = t('statements.unavailable');
  const civilRange = useCivilRange(dash);
  const periodLabel = (row: StatementView) => civilRange(row.periodStart, row.periodEnd);

  return (
    <Box>
      <PageHeader id="statements" />
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('statements.tenant.payoutsMoved')}{' '}
        <Link component={RouterLink} to="/settlements">
          {t('statements.tenant.payoutsLink')}
        </Link>
      </Alert>
      <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 1.5, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: FIELD_WIDTH.sm }}>
          <InputLabel id="tenant-statements-status-label">
            {t('statements.tenant.filterLabel')}
          </InputLabel>
          <Select
            labelId="tenant-statements-status-label"
            label={t('statements.tenant.filterLabel')}
            value={filter}
            onChange={(event) => url.set({ status: event.target.value as typeof filter })}
          >
            <MenuItem value="">{t('statements.tenant.all')}</MenuItem>
            {STATUSES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`subscribers.chargeStatus.${value}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: FIELD_WIDTH.sm }}>
          <InputLabel id="tenant-statements-period-label">
            {t('statements.tenant.periodLabel')}
          </InputLabel>
          <Select
            labelId="tenant-statements-period-label"
            label={t('statements.tenant.periodLabel')}
            value={period}
            onChange={(event) => url.set({ period: event.target.value })}
          >
            <MenuItem value="">{t('statements.tenant.allPeriods')}</MenuItem>
            {months.map((month) => (
              <MenuItem key={month} value={month}>
                {formatCivilMonth(month, locale)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      {status === 'loading' ? (
        <Box role="status" aria-label={t('common.loading')}>
          <Skeleton variant="rounded" height={240} />
        </Box>
      ) : null}
      {status === 'error' ? <ApiErrorState what={t('errors.what.statements')} error={error} onRetry={reload} /> : null}
      {status === 'ready' && rows.length === 0 && filtered ? (
        <EmptyState
          kind="noMatch"
          title={t('statements.tenant.noMatch')}
          onClear={() => url.set({ status: '', period: '' })}
        />
      ) : null}
      {status === 'ready' && rows.length === 0 && !filtered ? (
        <EmptyState
          kind="none"
          title={t('statements.tenant.empty')}
          body={t('statements.tenant.emptyBody')}
        />
      ) : null}
      {status === 'ready' && rows.length > 0 ? (
        <Paper sx={{ overflow: 'auto' }}>
          <Table size="small" aria-label={t('pages.statements.title')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('subscribers.stmt.period')}</TableCell>
                <TableCell align="right">{t('subscribers.stmt.seats')}</TableCell>
                <TableCell align="right">{t('subscribers.stmt.tax')}</TableCell>
                <TableCell align="right">{t('subscribers.stmt.total')}</TableCell>
                <TableCell>{t('subscribers.stmt.status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const currency = row.currency;
                const isSelected = row.id === selectedId;
                return (
                  <TableRow
                    key={row.id}
                    hover
                    selected={isSelected}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      navigate({
                        pathname: isSelected ? link('statements') : link(`statements/${row.id}`),
                        search,
                      });
                    }}
                  >
                    <TableCell component="th" scope="row">
                      <Link
                        component={RouterLink}
                        to={{ pathname: link(`statements/${row.id}`), search }}
                        aria-current={isSelected ? 'true' : undefined}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {periodLabel(row)}
                      </Link>
                    </TableCell>
                    <NumericCell value={row.seats} />
                    <MoneyCell minor={row.taxMinor} currency={currency} />
                    <MoneyCell minor={row.totalMinor} currency={currency} />
                    <TableCell>
                      <StatusChip domain="statement" status={row.chargeStatus} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      ) : null}
      {selected ? <StatementLines statement={selected} slug={tenant.slug} /> : null}
    </Box>
  );
}
